// A skill made from a Word document: the .docx goes in assets/, and SKILL.md
// tells the agent to take that document's look (fonts, colours, page setup,
// header/footer) whenever it writes this kind of document. What the old
// Templates tab did, as an ordinary skill.
import React, { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { FiLoader } from 'react-icons/fi';
import toast from 'react-hot-toast';

import Modal from '../base/Modal';
import * as skillsApi from '../../services/skillsApi';
import { errorMessage } from './skillsShared';

// Same rule as the backend's skill names (package.py _NAME_RE, 64 chars).
export const toSkillName = (title) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/, '');

export const buildSkillMd = ({ name, title, whenToUse, instructions, file }) =>
  [
    '---',
    `name: ${name}`,
    // JSON strings are valid YAML double-quoted strings.
    `description: ${JSON.stringify(whenToUse.trim())}`,
    '---',
    '',
    `# ${title.trim()}`,
    '',
    instructions.trim(),
    '',
    `When you write this kind of document, take its look from \`assets/${file}\`: ` +
      'create the Word document with ' +
      `\`import oqlous_docx as oq\` and ` +
      `\`oq.new_document(template='${file}')\`, then add the content. ` +
      'Never change the file itself.',
    '',
  ]
    .filter((line, i, all) => line !== '' || all[i - 1] !== '')
    .join('\n');

/** Renders a .docx Blob in the page (no download, nothing executed). */
export function DocxPreview({ blob }) {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!blob || !ref.current) return;
    ref.current.innerHTML = '';
    renderAsync(blob, ref.current, null, { inWrapper: true }).catch(() =>
      setFailed(true),
    );
  }, [blob]);

  return failed ? (
    <p className="text-xs text-zinc-400">This document can't be previewed.</p>
  ) : (
    <div
      ref={ref}
      className="max-h-[60vh] overflow-auto rounded-md bg-white text-black"
    />
  );
}

const field =
  'w-full rounded border border-zinc-700 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 outline-none focus:border-[#5652CF]';

export default function SkillFromDocument({
  isOpen,
  onClose,
  onCreated,
  accessToken,
}) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [whenToUse, setWhenToUse] = useState('');
  const [instructions, setInstructions] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setTitle('');
    setWhenToUse('');
    setInstructions('');
  }, [isOpen]);

  const name = toSkillName(title);
  const ready = file && name && whenToUse.trim() && !saving;

  const pickFile = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (!picked.name.toLowerCase().endsWith('.docx')) {
      toast.error('Choose a .docx file.');
      return;
    }
    setFile(picked);
    if (!title) setTitle(picked.name.replace(/\.docx$/i, ''));
  };

  const save = async () => {
    setSaving(true);
    try {
      const skillMd = buildSkillMd({
        name,
        title,
        whenToUse,
        instructions,
        file: file.name,
      });
      const { data: skill } = await skillsApi.importSkillEntries(accessToken, [
        {
          path: 'SKILL.md',
          blob: new Blob([skillMd], { type: 'text/markdown' }),
        },
        { path: `assets/${file.name}`, blob: file },
      ]);
      toast.success(`Created "${skill.name}".`);
      onCreated(skill);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not create the skill.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Skill from a Word document" isOpen={isOpen} onClose={onClose}>
      <div className="space-y-3 p-4 text-xs text-zinc-300">
        <p className="text-zinc-400">
          The agent writes documents of this kind in this document's look:
          fonts, colours, page setup, header and footer.
        </p>
        <label className="block space-y-1">
          <span>Word document (.docx)</span>
          <input
            type="file"
            accept=".docx"
            onChange={pickFile}
            className={field}
          />
        </label>
        {file && <DocxPreview blob={file} />}
        <label className="block space-y-1">
          <span>Name</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Quarterly business report"
            className={field}
          />
          {title && (
            <span className="text-[10px] text-zinc-500">
              Skill name: {name || 'use letters or numbers'}
            </span>
          )}
        </label>
        <label className="block space-y-1">
          <span>When should the agent use it?</span>
          <textarea
            value={whenToUse}
            onChange={(e) => setWhenToUse(e.target.value)}
            maxLength={1024}
            rows={2}
            placeholder="Use when the user asks for a quarterly business report."
            className={field}
          />
        </label>
        <label className="block space-y-1">
          <span>Instructions (optional)</span>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={3}
            placeholder="Keep the executive summary to one page."
            className={field}
          />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-zinc-700 px-3 py-1.5 text-zinc-300 hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!ready}
            onClick={save}
            className="flex items-center gap-1.5 rounded bg-[#5652CF] px-3 py-1.5 font-semibold text-white hover:bg-[#4642bf] disabled:opacity-50"
          >
            {saving && <FiLoader className="h-3 w-3 animate-spin" />}
            Create skill
          </button>
        </div>
      </div>
    </Modal>
  );
}
